import FusePageCarded from '@fuse/core/FusePageCarded';
import withReducer from 'app/store/withReducer';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import { FIELD_CONFIG, missingCreateUserFields } from 'app/constants/formFields';
import { getApiErrorMessage } from 'app/main/utils/apiErrors';
import _ from '@lodash';
import useUnsavedChangesGuard from 'app/main/utils/useUnsavedChangesGuard';

import reducer from '../store';
import FormGeneratorFormHeader from './FormGeneratorFormHeader';
import FormGeneratorForm from './FormGeneratorForm';

import { createForm, updateForm, getFormById, deleteForm, selectForm } from '../store/formGeneratorSlice';

const EMPTY_FORM = {
  name: '',
  description: '',
  slug: '',
  url: '',
  emailField: '',
  additionalInputs: [],
  optionalInputs: [],
  emailTemplates: [],
  secondaryEmailTemplates: [],
  submissionAction: 'none',
  active: false
};

function FormGeneratorFormPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const formData = useSelector(selectForm);

  const [form, setForm] = useState(EMPTY_FORM);
  const [baseline, setBaseline] = useState(EMPTY_FORM);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (isEdit) {
      dispatch(getFormById(id));
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (formData && isEdit) {
      const hydrated = {
        name: formData.name || '',
        description: formData.description || '',
        slug: formData.slug || '',
        url: formData.url || formData.slug || '',
        emailField: formData.emailField || '',
        additionalInputs: formData.additionalInputs || [],
        // Absent on every form built before optional inputs existed, which is
        // exactly the shape that keeps all of their fields required.
        optionalInputs: formData.optionalInputs || [],
        emailTemplates: formData.emailTemplates || [],
        secondaryEmailTemplates: formData.secondaryEmailTemplates || [],
        submissionAction: formData.submissionAction || 'none',
        active: Boolean(formData.active)
      };
      setForm(hydrated);
      setBaseline(hydrated);
    }
  }, [formData, isEdit]);

  const skipNextBlock = useUnsavedChangesGuard({
    isDirty: !_.isEqual(form, baseline),
    canSave: Boolean(String(form.name || '').trim()),
    saveBlockedHint: 'The form needs a name before it can be saved.',
    onSave: () => handleSave()
  });

  const handleSave = async () => {
    // A form that creates users but never collects their name or email would
    // only ever produce failed actions, so it is rejected before saving.
    if (form.submissionAction === 'createUser') {
      const missing = missingCreateUserFields(form);
      if (missing.length) {
        setSaveError(
          `To create a platform user this form also needs: ${missing
            .map(key => FIELD_CONFIG[key].label)
            .join(', ')}.`
        );
        return;
      }
    }

    // `meta.requestStatus` only exists from redux-toolkit 1.5 and this app pins
    // 1.4, where it is always undefined — reading it reported every save as a
    // failure, successful ones included. `match` compares action types instead.
    const save = isEdit ? updateForm : createForm;
    const result = await dispatch(isEdit ? save({ id, data: form }) : save(form));

    if (!save.fulfilled.match(result)) {
      setSaveError(getApiErrorMessage(result.payload, 'The form could not be saved. Please try again.'));
      return;
    }

    skipNextBlock();
    history.push('/form-generator');
  };

  const handleDelete = async () => {
    await dispatch(deleteForm(id));
    skipNextBlock();
    history.push('/form-generator');
  };

  return (
    <>
      <FusePageCarded
        classes={{
          content: 'flex',
          contentCard: 'w-full max-w-xl mx-auto mb-40',
          header: 'min-h-96 h-96 sm:h-136 sm:min-h-136 text-grey-800',
          topBg: 'light-header'
        }}
        header={
          <FormGeneratorFormHeader isEdit={isEdit} onSave={handleSave} onDelete={handleDelete} formName={form.name} />
        }
        content={<FormGeneratorForm form={form} setForm={setForm} />}
      />
      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={6000}
        onClose={() => setSaveError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSaveError('')}>
          {saveError}
        </Alert>
      </Snackbar>
    </>
  );
}

export default withReducer('settings', reducer)(FormGeneratorFormPage);
